/**
 * AzureBrowseButton — "Browse Azure" beside the connection form's server field. A labelled button with the Azure icon
 * on every engine Azure discovery covers (SQL Server, PostgreSQL, MySQL, Azure Data Explorer, Azure Storage; rule 3b.9 — the
 * PostgreSQL icon-only glyph is gone since Option C C2); every other engine renders nothing. `describedBy` points at the
 * field's label. Owns the picker's open state and scopes it to the form's engine (`engineFilter`); a pick is handed
 * back via `onPick`, which fills the form and places focus (the dialog's prefill rule: the first required empty field —
 * C3.1). A Cancel or Escape returns focus to this button itself (plan §3.6 nested focus return): Radix's modal Dialog
 * returns focus only to its own `Dialog.Trigger`, and the picker has none, so focus fell to <body>. Picker analytics
 * (open/pick) fire inside AzureResourcePicker — this button fires none itself.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ReasonButton } from '@/components/ui/ReasonButton';
import type { DatabaseEngine } from '../components/connectionFormSchema';
import type { AzureDiscoveredResource, AzureDiscoveryEngine } from '../../../types/azureDiscovery';
import { AzureResourcePicker } from './AzureResourcePicker';
import { AzureIcon } from './AzureIcon';

const AZURE_ENGINES: readonly DatabaseEngine[] = ['kusto', 'mssql', 'pgsql', 'mysql', 'azurestorage'];

function isAzureEngine(engine: DatabaseEngine): engine is AzureDiscoveryEngine {
  return AZURE_ENGINES.includes(engine);
}

interface AzureBrowseButtonProps {
  engine: DatabaseEngine;
  onPick: (resource: AzureDiscoveredResource) => void;
  /** Unavailable for now, with this reason (the connection dialog while a Save is in flight: "Saving…"). */
  disabledReason?: string;
  /** The id of the field label the button acts for (`aria-describedby`). */
  describedBy?: string;
}

export function AzureBrowseButton({ engine, onPick, disabledReason, describedBy }: AzureBrowseButtonProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  if (!isAzureEngine(engine)) return null;

  if (disabledReason) {
    return (
      <ReasonButton type="button" variant="outline" className="whitespace-nowrap" reason={disabledReason} aria-describedby={describedBy} data-testid="azure-browse-button">
        <AzureIcon />
        Browse Azure
      </ReasonButton>
    );
  }

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="whitespace-nowrap"
        aria-describedby={describedBy}
        data-testid="azure-browse-button"
      >
        <AzureIcon />
        Browse Azure
      </Button>

      {/* Mount only while open so the discovery query hooks don't run for a
          closed dialog (and stay out of contexts without a QueryClient). */}
      {open && (
        <AzureResourcePicker
          open
          onOpenChange={setOpen}
          engineFilter={engine}
          entry="form"
          onPick={onPick}
          onCloseAutoFocus={(event, outcome) => {
            // A pick: the form has placed focus (its first required empty field). Otherwise back to this button.
            event.preventDefault();
            if (outcome !== 'picked') buttonRef.current?.focus();
          }}
        />
      )}
    </>
  );
}
